import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiFileText, FiTrendingUp, FiFilter, FiPlus, FiX, FiUser, FiMail, FiPhone, FiMapPin, FiCalendar } = FiIcons;

const referralsData = [
  { id: 'R001', patient: 'Alice Johnson', status: 'In Progress', date: '2023-10-26', provider: 'Dr. Davis', type: 'Implant' },
  { id: 'R002', patient: 'Bob Williams', status: 'Pending', date: '2023-10-25', provider: 'Dr. Jones', type: 'Extraction' },
  { id: 'R003', patient: 'Charlie Brown', status: 'Completed', date: '2023-10-24', provider: 'Dr. Davis', type: 'Consult' },
  { id: 'R004', patient: 'Diana Miller', status: 'Declined', date: '2023-10-23', provider: 'Dr. Wilson', type: 'Implant' },
];

const statusColors = {
  'Pending': 'bg-yellow-500/20 text-yellow-300',
  'In Progress': 'bg-blue-500/20 text-blue-300',
  'Completed': 'bg-green-500/20 text-green-300',
  'Declined': 'bg-red-500/20 text-red-300',
};

const StatCard = ({ icon, title, value, change }) => (
  <motion.div
    className="bg-gray-800 p-6 rounded-xl flex items-center"
    whileHover={{ y: -5, boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05)' }}
  >
    <div className="bg-gray-700 p-4 rounded-lg mr-4">
      <SafeIcon icon={icon} className="text-3xl text-brand-primary" />
    </div>
    <div>
      <p className="text-gray-400 text-sm">{title}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
      {change && <p className={`text-sm ${change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>{change}</p>}
    </div>
  </motion.div>
);

const ReferralManagement = () => {
  const navigate = useNavigate();
  const [showNewReferralModal, setShowNewReferralModal] = useState(false);
  const [formData, setFormData] = useState({
    patientName: '',
    patientEmail: '',
    patientPhone: '',
    patientAddress: '',
    provider: '',
    caseType: '',
    notes: '',
    appointmentDate: ''
  });

  const handleRowClick = (id) => {
    navigate(`/referral-tracking/${id}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('New referral submitted:', formData);
    // Add your submission logic here
    setShowNewReferralModal(false);
    setFormData({
      patientName: '',
      patientEmail: '',
      patientPhone: '',
      patientAddress: '',
      provider: '',
      caseType: '',
      notes: '',
      appointmentDate: ''
    });
  };

  return (
    <div className="p-8 bg-gray-900 min-h-screen text-white">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="text-4xl font-bold mb-2">Referrals & Cases</h1>
        <p className="text-gray-400">Track and manage all incoming and ongoing cases.</p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
      >
        <StatCard icon={FiUsers} title="Total Referrals" value="1,284" change="+12% this month" />
        <StatCard icon={FiFileText} title="Active Cases" value="72" />
        <StatCard icon={FiTrendingUp} title="Conversion Rate" value="68%" change="-2% this month" />
      </motion.div>

      <div className="bg-gray-800 rounded-xl">
        <div className="p-6 flex justify-between items-center border-b border-gray-700">
          <h2 className="text-2xl font-semibold">Referral List</h2>
          <div className="flex items-center space-x-4">
            <button className="flex items-center bg-gray-700 px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">
              <SafeIcon icon={FiFilter} className="mr-2" />
              Filter
            </button>
            <button 
              onClick={() => setShowNewReferralModal(true)}
              className="flex items-center bg-brand-primary px-4 py-2 rounded-lg hover:bg-brand-primary-dark font-semibold transition-colors"
            >
              <SafeIcon icon={FiPlus} className="mr-2" />
              New Referral
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-gray-400 uppercase text-sm">
              <tr>
                <th className="p-4">Referral ID</th>
                <th className="p-4">Patient</th>
                <th className="p-4">Status</th>
                <th className="p-4">Date</th>
                <th className="p-4">Provider</th>
                <th className="p-4">Case Type</th>
              </tr>
            </thead>
            <tbody>
              {referralsData.map((ref, index) => (
                <motion.tr
                  key={ref.id}
                  className="border-t border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  onClick={() => handleRowClick(ref.id)}
                >
                  <td className="p-4 font-mono text-gray-500">{ref.id}</td>
                  <td className="p-4 font-semibold">{ref.patient}</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[ref.status]}`}>
                      {ref.status}
                    </span>
                  </td>
                  <td className="p-4 text-gray-300">{ref.date}</td>
                  <td className="p-4 text-gray-300">{ref.provider}</td>
                  <td className="p-4 text-gray-300">{ref.type}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Referral Modal */}
      <AnimatePresence>
        {showNewReferralModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewReferralModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Create New Referral</h2>
                <button
                  onClick={() => setShowNewReferralModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Patient Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <SafeIcon icon={FiUser} className="mr-2" />
                    Patient Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Patient Name *</label>
                      <input
                        type="text"
                        name="patientName"
                        value={formData.patientName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                      <input
                        type="email"
                        name="patientEmail"
                        value={formData.patientEmail}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="patientPhone"
                        value={formData.patientPhone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                      <input
                        type="text"
                        name="patientAddress"
                        value={formData.patientAddress}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        placeholder="123 Main St, City, State"
                      />
                    </div>
                  </div>
                </div>

                {/* Referral Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <SafeIcon icon={FiFileText} className="mr-2" />
                    Referral Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Referring Provider *</label>
                      <select
                        name="provider"
                        value={formData.provider}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      >
                        <option value="">Select provider</option>
                        <option value="Dr. Davis">Dr. Davis</option>
                        <option value="Dr. Jones">Dr. Jones</option>
                        <option value="Dr. Wilson">Dr. Wilson</option>
                        <option value="Dr. Smith">Dr. Smith</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Case Type *</label>
                      <select
                        name="caseType"
                        value={formData.caseType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      >
                        <option value="">Select case type</option>
                        <option value="Implant">Implant</option>
                        <option value="Extraction">Extraction</option>
                        <option value="Consult">Consult</option>
                        <option value="Root Canal">Root Canal</option>
                        <option value="Orthodontics">Orthodontics</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Appointment Date</label>
                      <input
                        type="date"
                        name="appointmentDate"
                        value={formData.appointmentDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white resize-none"
                        placeholder="Additional information about the referral..."
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowNewReferralModal(false)}
                    className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    <span>Create Referral</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferralManagement;